import React, {
  ButtonHTMLAttributes,
  CSSProperties,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import clsx from "clsx";

import Spinner from "../Spinner/Spinner";
import styles from "./Button.module.css";
import { getButtonStyles } from "~/theme/models/themeModels";
import ThemeContext from "~/contexts/ThemeContext";
import { Shape, Size } from "~/entities/templateEntities";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "counter-primary"
  | "counter-secondary"
  | "link";

export type ButtonTheme = {
  color: string;
  borderColor: string;
  fontColor: string;
  hoverColor: string;
  hoverBorderColor: string;
  hoverFontColor: string;
  size: Size;
  style: Shape;
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  loading?: boolean;
  theme?: ButtonTheme;
};

const Button: React.FC<PropsWithChildren<ButtonProps>> = ({
  variant = "primary",
  loading = false,
  children,
  theme,
  ...props
}) => {
  const { template } = useContext(ThemeContext);

  const buttonStyles: CSSProperties | undefined = useMemo(() => {
    let baseStyles: CSSProperties = {};
    let otherStyles: Record<string, string> = {};

    if (theme) {
      baseStyles = getButtonStyles(
        theme.color,
        theme.borderColor,
        theme.fontColor,
        theme.size,
        theme.style,
      );
      otherStyles = {
        "--hover-button-colour": theme.hoverColor,
        "--hover-button-border-colour": theme.hoverBorderColor,
        "--hover-button-font-colour": theme.hoverFontColor,
      };
    } else if (variant === "primary") {
      baseStyles = getButtonStyles(
        template.global.button.primaryButtonColour,
        template.global.button.primaryButtonBorderColour,
        template.global.button.primaryButtonFontColour,
        template.global.button.buttonSize,
        template.global.button.buttonStyle,
      );
      otherStyles = {
        "--hover-button-colour":
          template.global.button.hoverPrimaryButtonColour,
        "--hover-button-border-colour":
          template.global.button.hoverPrimaryButtonBorderColour,
        "--hover-button-font-colour":
          template.global.button.hoverPrimaryButtonFontColour,
      };
    } else if (variant === "secondary") {
      baseStyles = getButtonStyles(
        template.global.button.secondButtonColour,
        template.global.button.secondButtonBorderColour,
        template.global.button.secondButtonFontColour,
        template.global.button.buttonSize,
        template.global.button.buttonStyle,
      );
      otherStyles = {
        "--hover-button-colour": template.global.button.hoverSecondButtonColour,
        "--hover-button-border-colour":
          template.global.button.hoverSecondButtonBorderColour,
        "--hover-button-font-colour":
          template.global.button.hoverSecondButtonFontColour,
      };
    } else if (variant === "link") {
      baseStyles = {
        color: template.global.button.primaryButtonColour,
      };
    } else if (variant == "counter-primary") {
      baseStyles = {
        backgroundColor: template.global.button.primaryButtonColour,
        borderColor: template.global.button.primaryButtonBorderColour,
        color: template.global.button.primaryButtonFontColour,
      };
      otherStyles = {
        "--button-color": template.global.button.primaryButtonColour,
        "--button-border-colour":
          template.global.button.primaryButtonBorderColour,
        "--button-font-colour": template.global.button.primaryButtonFontColour,
        "--hover-button-colour":
          template.global.button.hoverPrimaryButtonColour,
        "--hover-button-border-colour":
          template.global.button.hoverPrimaryButtonBorderColour,
        "--hover-button-font-colour":
          template.global.button.hoverPrimaryButtonFontColour,
      };
    } else if (variant == "counter-secondary") {
      baseStyles = {
        backgroundColor: template.global.button.secondButtonColour,
        borderColor: template.global.button.secondButtonBorderColour,
        color: template.global.button.secondButtonFontColour,
      };
      otherStyles = {
        "--button-color": template.global.button.secondButtonColour,
        "--button-border-colour":
          template.global.button.secondButtonBorderColour,
        "--button-font-colour": template.global.button.secondButtonFontColour,
        "--hover-button-colour": template.global.button.hoverSecondButtonColour,
        "--hover-button-border-colour":
          template.global.button.hoverSecondButtonBorderColour,
        "--hover-button-font-colour":
          template.global.button.hoverSecondButtonFontColour,
      };
    }

    return {
      ...baseStyles,
      ...otherStyles,
    };
  }, [
    template.global.button.buttonSize,
    template.global.button.buttonStyle,
    template.global.button.hoverPrimaryButtonBorderColour,
    template.global.button.hoverPrimaryButtonColour,
    template.global.button.hoverPrimaryButtonFontColour,
    template.global.button.hoverSecondButtonBorderColour,
    template.global.button.hoverSecondButtonColour,
    template.global.button.hoverSecondButtonFontColour,
    template.global.button.primaryButtonBorderColour,
    template.global.button.primaryButtonColour,
    template.global.button.primaryButtonFontColour,
    template.global.button.secondButtonBorderColour,
    template.global.button.secondButtonColour,
    template.global.button.secondButtonFontColour,
    theme,
    variant,
  ]);

  return (
    <button
      {...props}
      disabled={props.disabled || loading}
      className={clsx(
        styles.wrapper,
        variant === "primary" && styles.primary,
        variant === "secondary" && styles.secondary,
        (variant === "counter-primary" || variant === "counter-secondary") &&
          styles.counter,
        variant === "link" && styles.link,
        props.disabled &&
          variant !== "link" &&
          !loading &&
          styles.disabledNoLinkNoLoading,
        loading && styles.loading,
        props.className,
      )}
      style={{
        ...buttonStyles,
        ...props.style,
      }}
    >
      {loading && (
        <Spinner
          className={styles.spinner}
          style={{
            width: 30,
            height: 30,
            borderLeftColor: buttonStyles.color,
          }}
        />
      )}
      {children}
    </button>
  );
};

export default Button;
